/**
 * mobile-bar.js — the phone's bottom bar, and its two sheets.
 *
 * ── The structural decision this file exists to make ──────────────────────
 * The bottom bar does **not** rebuild row 2. Row 2 *becomes* the bottom bar:
 * on a phone the SAME ELEMENT is re-parented to `document.body` and pinned to
 * the bottom, and everything inside it — the panes, the morph, the live region,
 * both anchors — travels with it.
 *
 * CSS alone could not do it. `.pb-cb` carries `container-type: inline-size` and
 * `backdrop-filter`, and either one establishes a containing block for
 * fixed-position descendants — so `position: fixed; bottom: 0` on `.pb-cb-row2`
 * resolved against the BAR rather than the viewport, and the row stayed pinned
 * under row 1 looking exactly like a media query that had not matched. See
 * `_applyPlacement`.
 *
 * The plan asks for "exact action parity with desktop, from the CMD-0 matrix".
 * Parity that is maintained is parity that drifts; parity that is the SAME DOM
 * cannot. Two initiatives in this repo are "code complete, device gate pending"
 * partly because their phone surface was a second implementation nobody
 * exercised, and a second bottom bar would have been a third.
 *
 * What this module adds is the two things the phone needs that the desktop does
 * not:
 *
 *   · **Pages** — the tab strip cannot fit, so it becomes a sheet.
 *   · **More**  — what the top strip drops (save state, Share, the agent, the
 *     account) has to be reachable, so it becomes a sheet.
 *
 * ── Height is published, never assumed ────────────────────────────────────
 * A fixed bar at the bottom occludes the canvas, drawers, sheets, popovers, the
 * comments rail, toasts and the docked agent. `--pb-command-bottom-height-actual`
 * is measured from a `ResizeObserver` — the same mechanism
 * `--pb-toolbar-height-actual` uses — because the bar's height is a function of
 * the safe-area inset and the coarse-pointer register, and neither is knowable
 * from CSS alone.
 *
 * It is published as `0px` on desktop rather than left unset: a consumer that
 * writes `calc(… + var(--pb-command-bottom-height-actual))` must resolve on
 * every viewport, and an unset variable makes the whole `calc()` invalid — which
 * fails by silently dropping the declaration, the least debuggable outcome.
 */

import {
  SAVE_LABELS,
  PROJECT_LABELS,
  PUBLISH_LABELS,
  HELP_LABELS
} from '../../shared/ui-vocabulary.js';
import { eventBus } from '../../core/events.js';
import { store } from '../../core/state.js';
import { createButton } from '../components.js';
import { createSheetDrawer, createSegmentedControl } from '../components.js';
import { VIEWPORTS, getActiveViewportName } from '../viewport-controls.js';

/** BC-10: one sentence for the cloud prerequisite, visible and in the tooltip. */
const SHARE_REQUIRES_CLOUD_REASON = 'Save this project to your account first';
import { icon } from '../icons.js';
import { showRenamePageDialog } from './rename-page-dialog.js';
import { buildPageActions } from '../page-actions.js';
import { isAgentReachable } from '../agent-chat/agent-origin.js';
import { SHARE_ACTIONS } from './share-actions.js';
import {
  isPhoneViewport,
  onViewportChange,
  getViewportHeight
} from '../../shared/viewport-service.js';

/** The custom property every bottom-anchored surface offsets itself by. */
export const BOTTOM_HEIGHT_VAR = '--pb-command-bottom-height-actual';

export class MobileBar {
  /**
   * @param {HTMLElement} row2 — `.pb-cb-row2`. Measured, never rebuilt.
   * @param {HTMLElement} host — where the phone-only triggers mount (row 2's
   *   left anchor, so they sit beside undo/redo rather than in the morph zone,
   *   which has to stay free to change).
   */
  constructor(row2, host, { initialAuthPayload = null } = {}) {
    this._row2 = row2;
    this._host = host;
    // Where row 2 lives on desktop, so it can be put back.
    this._deskParent = row2.parentElement;
    this._deskNext = row2.nextSibling;
    this._observer = null;
    this._offs = [];
    this._sheet = null;
    this._lifetime = new AbortController();
    this._authPayload = initialAuthPayload;
    // SH-1/D1. Mirrors the desktop's own starting position: `syncCommentMenuItems`
    // is first called with no payload, so `disabled` is undefined and the rows
    // show until a poll actually 503s. Starting `true` would hide them on a
    // healthy deployment for the whole first render.
    this._commentsDisabled = false;
    this._render();
    this._listen();
    this._applyPlacement();
    this._publishHeight();
  }

  destroy() {
    this._lifetime.abort();
    this._previewExit?.remove();
    this._previewExit = null;
    this._observer?.disconnect();
    this._observer = null;
    for (const off of this._offs) if (typeof off === 'function') off();
    this._offs = [];
    this._sheet?.close();
    this._sheet = null;
    // Put row 2 back before letting go of it, or a re-mount finds it orphaned
    // on `document.body` with no bar to return to.
    if (this._row2.parentElement === document.body) {
      this._deskParent?.insertBefore(this._row2, this._deskNext);
      delete this._row2.dataset.pbPlacement;
    }
    document.documentElement.style.removeProperty(BOTTOM_HEIGHT_VAR);
  }

  _render() {
    this._pagesBtn = createButton({
      variant: 'bare',
      className: 'pb-cb-icon pb-cb-phone-only',
      icon: icon('templates', { size: 'sm' }),
      ariaLabel: 'Pages',
      tooltip: 'Pages',
      ariaHaspopup: 'dialog',
      onClick: () => this._openSheet('pages')
    });
    this._moreBtn = createButton({
      variant: 'bare',
      className: 'pb-cb-icon pb-cb-phone-only',
      icon: icon('sliders', { size: 'sm' }),
      ariaLabel: 'More',
      tooltip: 'More',
      ariaHaspopup: 'dialog',
      onClick: () => this._openSheet('more')
    });
    this._host.append(this._pagesBtn, this._moreBtn);
  }

  _listen() {
    if (typeof ResizeObserver === 'function') {
      this._observer = new ResizeObserver(() => this._publishHeight());
      this._observer.observe(this._row2);
    }
    // Rotation and the virtual keyboard both change the bar's position without
    // changing its size, and neither fires a resize on the element itself.
    // Crossing the phone threshold also changes where the bar BELONGS.
    this._offs.push(
      onViewportChange(() => {
        this._applyPlacement();
        this._publishHeight();
      })
    );
    this._offs.push(
      eventBus.on('auth:state', (payload) => {
        this._authPayload = payload;
      })
    );
    // SH-1/D1. The same signal `shell.js` reads to hide the desktop menu's
    // three collaboration ids. Cached here rather than imported from
    // `comment-controller.js`, whose module graph reaches `editing/bridge.js` —
    // this sheet is built fresh on every open, so it only needs the answer, and
    // taking it from the event keeps ONE mechanism rather than a second reader
    // of the same latch.
    this._offs.push(
      eventBus.on('comments:changed', ({ disabled } = {}) => {
        this._commentsDisabled = !!disabled;
      })
    );
    // BC-10: preview needs an exit that is ON SCREEN. It lived only inside
    // More, under a row that still read "Preview" while previewing.
    this._previewing =
      document.getElementById('pb-preview-btn')?.getAttribute('aria-pressed') === 'true';
    this._offs.push(
      eventBus.on('preview:state-changed', ({ enabled } = {}) => {
        this._previewing = Boolean(enabled);
        this._syncPreviewExit();
      })
    );
    // BC-9: the palette's page-actions command, on a phone.
    this._offs.push(
      eventBus.on('mobile-bar:open-page-actions', ({ pageId } = {}) => {
        if (pageId) this._openSheet('page-actions', pageId);
      })
    );
  }

  /** Whether comment-specific rows belong in the More sheet. */
  _collabRowsVisible() {
    return !!Number(store.getState()?.cloudVersion) && !this._commentsDisabled;
  }

  /**
   * Move row 2 out of `.pb-cb` on a phone, and back on a desktop.
   *
   * ── Why a CSS-only `position: fixed` does not work ────────────────────────
   * `.pb-cb` carries `container-type: inline-size` (for the bar's own container
   * queries) and `backdrop-filter`. **Either one establishes a containing block
   * for fixed-position descendants.** So `position: fixed; bottom: 0` on
   * `.pb-cb-row2` resolves against the bar, not the viewport — the row stayed
   * pinned under row 1 and looked like the phone CSS had simply not applied.
   *
   * Found by the phone geometry gate reporting `row2Bottom: 46` against a
   * viewport of 844.
   *
   * Re-parenting is the fix that keeps the phase's structural promise: it moves
   * the SAME ELEMENT, so the panes, the morph, the live region and both anchors
   * travel with it and there is still exactly one action bar. Rebuilding a
   * bottom bar here is what this whole file exists not to do.
   */
  _applyPlacement() {
    const phone = isPhoneViewport();
    const inBody = this._row2.parentElement === document.body;
    if (phone && !inBody) {
      document.body.appendChild(this._row2);
      this._row2.dataset.pbPlacement = 'bottom';
    } else if (!phone && inBody) {
      // Back to exactly where it was, not merely back inside the bar: row 2
      // must stay AFTER row 1, or the reading order inverts.
      this._deskParent?.insertBefore(this._row2, this._deskNext);
      delete this._row2.dataset.pbPlacement;
    }
  }

  /**
   * Measure and publish. `0px` off-phone, because the bar is in flow there and
   * occludes nothing.
   */
  _publishHeight() {
    const height = isPhoneViewport() ? Math.round(this._row2.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty(BOTTOM_HEIGHT_VAR, `${height}px`);
  }

  /** For tests. */
  get publishedHeight() {
    return document.documentElement.style.getPropertyValue(BOTTOM_HEIGHT_VAR);
  }

  // ── the two sheets ────────────────────────────────────────────────────────

  _openSheet(kind, arg) {
    // One sheet at a time. Opening Pages over More would leave two non-modal
    // cards stacked with no way to tell which owns the canvas behind them.
    this._sheet?.close();

    const spec =
      kind === 'pages'
        ? this._pagesSheet()
        : kind === 'page-actions'
          ? this._pageActionsSheet(arg)
          : kind === 'share'
            ? this._shareSheet()
            : kind === 'view'
              ? this._viewSheet()
              : this._moreSheet();
    if (!spec) return null;
    const sheet = createSheetDrawer({
      id: `pb-cb-sheet-${kind}`,
      title: spec.title,
      detent: 'half',
      // The sheet sits ABOVE the bottom bar, so its usable height is the
      // viewport minus the bar. Passing `window.innerHeight` would let the
      // `full` detent extend behind the bar and hide its own last row.
      usableHeight: () => Math.max(0, getViewportHeight() - this._barHeight()),
      onClose: () => {
        this._sheet = null;
        spec.trigger?.focus?.();
      }
    });
    sheet.body.appendChild(spec.content);
    document.body.appendChild(sheet.sheet);
    this._sheet = sheet;
    return sheet;
  }

  _barHeight() {
    const raw = document.documentElement.style.getPropertyValue(BOTTOM_HEIGHT_VAR);
    return Number.parseFloat(raw) || 0;
  }

  /**
   * Pages. The same commands the desktop tab strip emits — `toolbar:switch-page`
   * and `toolbar:add-page` — so a page switch is one behaviour with two
   * affordances rather than two behaviours that look alike.
   */
  _pagesSheet() {
    const content = document.createElement('div');
    content.className = 'pb-cb-sheet-list';
    const state = store.getState();
    const pages = Array.isArray(state?.pages) ? state.pages : [];

    for (const page of pages) {
      const isActive = page.id === state?.activePage;
      const row = createButton({
        variant: 'bare',
        className: 'pb-cb-sheet-row',
        label: page.name,
        labelClass: 'pb-btn-label',
        ariaLabel: isActive ? `${page.name} (current page)` : page.name,
        icon: isActive ? icon('check', { size: 'sm' }) : undefined,
        onClick: () => {
          eventBus.emit('toolbar:switch-page', { pageId: page.id });
          this._sheet?.close();
        }
      });
      if (isActive) row.setAttribute('aria-current', 'page');
      const group = document.createElement('div');
      group.className = 'pb-cb-sheet-page';
      group.setAttribute('role', 'group');
      group.setAttribute('aria-label', page.name);
      group.append(
        row,
        createButton({
          variant: 'bare',
          className: 'pb-cb-sheet-page-actions',
          icon: icon('sliders', { size: 'sm' }),
          ariaLabel: `Page actions: ${page.name}`,
          ariaHaspopup: 'dialog',
          onClick: () => this._openSheet('page-actions', page.id)
        })
      );
      content.appendChild(group);
    }

    content.appendChild(
      createButton({
        variant: 'bare',
        className: 'pb-cb-sheet-row',
        icon: icon('plus', { size: 'sm' }),
        label: 'New page',
        labelClass: 'pb-btn-label',
        ariaLabel: 'Add page',
        onClick: () => {
          eventBus.emit('toolbar:add-page');
          this._sheet?.close();
        }
      })
    );
    return { title: 'Pages', content, trigger: this._pagesBtn };
  }

  /**
   * BC-9: rendered from the ONE page-actions model (`ui/page-actions.js`), so
   * the phone, the tab strip's menu and the palette offer the same actions
   * with the same reasons. A disabled row SHOWS its reason on a line under it:
   * on a touch screen there is no tooltip to hover.
   */
  _pageActionsSheet(pageId) {
    const model = buildPageActions(pageId, {
      orientation: 'vertical',
      onRename: (id) => {
        const target = (store.getState()?.pages || []).find((page) => page.id === id);
        if (!target) return;
        void showRenamePageDialog({ name: target.name, signal: this._lifetime.signal }).then(
          (name) => {
            if (name && (store.getState()?.pages || []).some((page) => page.id === id)) {
              eventBus.emit('toolbar:rename-page', { pageId: id, name });
            }
          }
        );
      }
    });
    if (!model) return null;
    const content = document.createElement('div');
    content.className = 'pb-cb-sheet-list';
    for (const action of model.actions) {
      const button = createButton({
        variant: 'bare',
        className: 'pb-cb-sheet-row',
        icon: icon(action.glyph, { size: 'sm' }),
        label: action.label,
        labelClass: 'pb-btn-label',
        ariaLabel: action.reason ? `${action.label} — ${action.reason}` : action.label,
        disabled: Boolean(action.reason),
        onClick: () => {
          // Close first: rename opens a dialog, and a sheet left open under it
          // would own the focus the dialog needs.
          this._sheet?.close();
          action.run();
        }
      });
      if (action.reason) button.title = action.reason;
      content.append(button);
      if (action.reason) {
        const hint = document.createElement('p');
        hint.className = 'pb-cb-sheet-row-hint pb-cb-sheet-row-reason';
        hint.textContent = action.reason;
        content.append(hint);
      }
    }
    return { title: `Page actions: ${model.page.name}`, content, trigger: this._pagesBtn };
  }

  /**
   * Share (FN-8). The SAME five actions the desktop Share menu renders, from
   * the same definitions in `share-actions.js`, hints included.
   *
   * This sheet is why the account menu could lose its "My previews" row: on a
   * phone that row was the only route to saved preview links, and the desktop
   * Share menu is not reachable there. Consolidating first and removing second
   * is the order that does not strand anyone.
   */
  _shareSheet() {
    const content = document.createElement('div');
    content.className = 'pb-cb-sheet-list';
    const collab = this._collabRowsVisible();

    const cloud = !!Number(store.getState()?.cloudVersion);
    let anyUnavailable = false;
    for (const action of SHARE_ACTIONS) {
      if (action.requires === 'collab' && !collab) continue;
      // FN-8: the shared definitions DECLARE a cloud requirement and this is
      // where the phone consumes it. Declaring a capability that no layout
      // reads is a contract nobody keeps: a local-only project offered these
      // rows and failed after the tap instead of saying why before it.
      const unavailable = action.requires === 'cloud' && !cloud;
      const button = createButton({
        variant: 'bare',
        className: 'pb-cb-sheet-row',
        icon: action.glyph ? icon(action.glyph, { size: 'sm' }) : undefined,
        label: action.label,
        labelClass: 'pb-btn-label',
        // The hint is part of the accessible name here rather than a second
        // line of chrome: a sheet row is already a wide target, and a screen
        // reader user needs the recipient capability at the same moment a
        // sighted one reads it.
        ariaLabel: unavailable
          ? `${action.label} — unavailable until this project is saved to your account`
          : `${action.label} — ${action.hint}`,
        disabled: unavailable,
        onClick: () => {
          eventBus.emit(action.event, action.payload);
          this._sheet?.close();
        }
      });
      if (unavailable) {
        // Stated refusal, on the control, before the tap — the row-2 pattern.
        button.setAttribute('aria-disabled', 'true');
        button.dataset.tooltip = SHARE_REQUIRES_CLOUD_REASON;
      }
      const hint = document.createElement('span');
      hint.className = 'pb-cb-sheet-row-hint';
      // BC-10: a refused row's visible line is its REASON. It showed the
      // recipient capability of a link the user could not make, while the
      // reason sat in a tooltip a finger cannot open.
      hint.textContent = unavailable ? SHARE_REQUIRES_CLOUD_REASON : action.hint;
      button.appendChild(hint);
      content.append(button);
      anyUnavailable ||= unavailable;
    }
    if (anyUnavailable) {
      // ...and the route that resolves it, in the same sheet.
      const account = this._hasAccount();
      content.append(
        createButton({
          variant: 'bare',
          className: 'pb-cb-sheet-row',
          icon: icon('cloud', { size: 'sm' }),
          label: 'Save to your account',
          labelClass: 'pb-btn-label',
          ariaLabel: account ? 'Save to your account' : 'Save to your account — sign in first',
          onClick: () => {
            this._sheet?.close();
            eventBus.emit(account ? 'toolbar:cloud-save' : 'toolbar:auth-show');
          }
        })
      );
    }
    return { title: 'Share', content, trigger: this._moreBtn };
  }

  /**
   * More. Everything the top strip drops on a phone.
   *
   * Each row emits the SAME command its desktop control does — that is what the
   * CMD-0 parity matrix means in practice, and it is why this sheet contains no
   * logic of its own. A row that computed anything would be a second
   * implementation of a desktop behaviour.
   */
  _moreSheet() {
    const content = document.createElement('div');
    content.className = 'pb-cb-sheet-list';

    // Keep literal tuples: command/census scans read them; runtime parity is tested.
    const rows = [
      // FN-8: Share opens the SHEET, not the access dialog. There are five ways
      // to let someone see this project and a phone used to reach exactly one
      // of them from here.
      [PUBLISH_LABELS.shareMenu, 'share', 'mobile:open-share-sheet'],
      // The account row sits directly after the collaboration block. SH-1
      // composes it IN PLACE rather than splicing it at index 4: the block
      // above it is conditional now, so a fixed index moves under it. The test
      // that claimed to prove the old anchor ran with NO account, so
      // `_hasAccount()` was false and nothing was ever spliced — it proved
      // nothing, and the ordering assertion it implied is added in this phase.
      ...(this._hasAccount() ? [['Account', 'profile', 'toolbar:open-account']] : []),
      // Parity with the desktop trigger, which is ABSENT rather than disabled
      // when the localhost proxy cannot be reached from this origin. A row that
      // opens a panel guaranteed to read "offline" is not parity.
      ...(isAgentReachable() ? [['PageStitch Agent', 'skin', 'agent:toggle']] : []),
      [SAVE_LABELS.history, 'history', 'command-bar:open-save-popover'],
      // BC-10: the device switcher and zoom left row 2 on a phone, where they
      // pushed the bar to 461px of controls in a 390px screen.
      ['View', 'desktop', 'mobile:open-view-sheet'],
      [PROJECT_LABELS.templates, 'templates', 'toolbar:open-templates'],
      [PROJECT_LABELS.links, 'links', 'toolbar:open-link-manager'],
      [HELP_LABELS.find, 'search', 'toolbar:open-command-palette'],
      // CMD-8. Both are in row 2's right anchor on a desktop and both are
      // hidden on a phone, where that anchor is already carrying the device
      // switcher and the zoom chip across 390px. Parity is what this sheet is
      // for; a control with no phone route is a control a phone user has lost.
      [this._previewing ? 'Exit preview' : 'Preview', 'play', 'toolbar:toggle-preview'],
      [HELP_LABELS.shortcuts, 'shortcuts', 'toolbar:show-shortcuts'],
      [HELP_LABELS.tour, 'tour', 'cmd:open-tour'],
      [HELP_LABELS.report, 'flag', 'toolbar:report-issue']
    ];

    for (const [label, glyph, command] of rows) {
      const button = createButton({
        variant: 'bare',
        className: 'pb-cb-sheet-row',
        icon: icon(glyph, { size: 'sm' }),
        label,
        labelClass: 'pb-btn-label',
        ariaLabel: label,
        onClick: () => {
          // The one row that opens another sheet rather than emitting a
          // command. Handled here instead of through the bus because a sheet is
          // this component's own surface, not a shell-owned one.
          if (command === 'mobile:open-share-sheet') {
            this._openSheet('share');
            return;
          }
          if (command === 'mobile:open-view-sheet') {
            this._openSheet('view');
            return;
          }
          if (command === 'command-bar:open-save-popover') {
            this._sheet?.close();
            eventBus.emit(command, { returnFocusTo: this._moreBtn });
            return;
          }
          eventBus.emit(command);
          this._sheet?.close();
        }
      });
      if (command === 'toolbar:toggle-preview') {
        button.setAttribute('aria-pressed', String(this._previewing));
      }
      content.append(button);
    }
    return { title: 'More', content, trigger: this._moreBtn };
  }

  /**
   * BC-10. The device switcher and zoom, moved off row 2 on a phone. Same
   * commands their desktop controls answer — `toolbar:set-viewport` and the
   * three zoom commands — so this sheet has no behaviour of its own.
   */
  _viewSheet() {
    const content = document.createElement('div');
    content.className = 'pb-cb-sheet-list pb-cb-view-sheet';
    const devices = createSegmentedControl({
      ariaLabel: 'Viewport size',
      className: 'pb-cb-view-devices',
      value: getActiveViewportName(),
      options: VIEWPORTS.map((vp) => ({
        value: vp.name,
        label: vp.label,
        icon: icon(vp.name, { size: 'sm' }),
        ariaLabel: vp.label
      })),
      onChange: (name) => eventBus.emit('toolbar:set-viewport', { name })
    });
    const zoom = document.createElement('div');
    zoom.className = 'pb-cb-view-zoom';
    zoom.setAttribute('role', 'group');
    zoom.setAttribute('aria-label', 'Zoom');
    for (const [label, glyph, command] of [
      ['Zoom out', 'minus', 'toolbar:zoom-out'],
      ['Reset zoom', 'search', 'toolbar:zoom-reset'],
      ['Zoom in', 'plus', 'toolbar:zoom-in']
    ]) {
      zoom.append(
        createButton({
          variant: 'bare',
          className: 'pb-cb-sheet-row pb-cb-view-zoom-btn',
          icon: icon(glyph, { size: 'sm' }),
          label: label === 'Reset zoom' ? '100%' : undefined,
          labelClass: 'pb-btn-label',
          ariaLabel: label,
          onClick: () => eventBus.emit(command)
        })
      );
    }
    content.append(devices.el, zoom);

    // The scrolled-header preview toggle left the phone bar with the rest of
    // the anchor. Its availability and state are the desktop control's own —
    // mirrored, never re-derived.
    const source = document.getElementById('pb-scroll-state-toggle');
    if (source) {
      const reason = source.disabled ? source.title : '';
      const scrolled = createButton({
        variant: 'bare',
        className: 'pb-cb-sheet-row',
        icon: icon('scrollState', { size: 'sm' }),
        label: 'Scrolled header',
        labelClass: 'pb-btn-label',
        ariaLabel: reason ? `Scrolled header — ${reason}` : 'Scrolled header',
        disabled: Boolean(reason),
        onClick: () => {
          eventBus.emit('toolbar:toggle-scroll-state');
          scrolled.setAttribute('aria-pressed', source.getAttribute('aria-pressed') || 'false');
        }
      });
      scrolled.setAttribute('aria-pressed', source.getAttribute('aria-pressed') || 'false');
      content.append(scrolled);
      if (reason) {
        const hint = document.createElement('p');
        hint.className = 'pb-cb-sheet-row-hint pb-cb-sheet-row-reason';
        hint.textContent = reason;
        content.append(hint);
      }
    }
    return { title: 'View', content, trigger: this._moreBtn };
  }

  /** BC-10: an on-screen way out of preview, mounted only while previewing. */
  _syncPreviewExit() {
    if (!this._previewing) {
      this._previewExit?.remove();
      this._previewExit = null;
      return;
    }
    if (this._previewExit?.isConnected) return;
    this._previewExit = createButton({
      variant: 'primary',
      pill: true,
      className: 'pb-cb-preview-exit pb-cb-phone-only',
      icon: icon('arrowLeft', { size: 'sm' }),
      label: 'Back to editing',
      testid: 'phone-preview-exit',
      onClick: () => eventBus.emit('toolbar:toggle-preview')
    });
    document.body.appendChild(this._previewExit);
  }

  _hasAccount() {
    const payload = this._authPayload;
    const isRealAccount = Boolean(
      payload && payload.sub && payload.authMode !== 'single-user' && payload.sub !== 'admin'
    );
    const forcedForDev =
      typeof window !== 'undefined' && window.__PB_FORCE_AUTH_UI === true && Boolean(payload?.sub);
    return isRealAccount || forcedForDev;
  }
}
